import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { RefreshCw, Calendar, ShieldAlert, CheckCircle2, Map as MapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../i18n/LanguageContext';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { ScoreCard } from './ScoreCard';
import { FaceHeatmap } from './FaceHeatmap';
import { analyzeFace, Point, Priority } from './analyzeFace';
interface ResultsStepProps {
  imageSrc: string;
  landmarks: Point[];
  onRetake: () => void;
}
const RECOMMENDATION_META: Record<string, {
  titleKey: string;
  descKey: string;
  product: string;
}> = {
  cheeks: {
    titleKey: 'faceAnalysis.results.cheeks',
    descKey: 'faceAnalysis.results.cheeksDesc',
    product: 'Singderm® Silk'
  },
  lips: {
    titleKey: 'faceAnalysis.results.lips',
    descKey: 'faceAnalysis.results.lipsDesc',
    product: 'Singderm® Silk'
  },
  nasolabial: {
    titleKey: 'faceAnalysis.results.nasolabial',
    descKey: 'faceAnalysis.results.nasolabialDesc',
    product: 'Singderm®'
  },
  jawline: {
    titleKey: 'faceAnalysis.results.jawline',
    descKey: 'faceAnalysis.results.jawlineDesc',
    product: 'Singderm®'
  },
  symmetry: {
    titleKey: 'faceAnalysis.results.symmetryRec',
    descKey: 'faceAnalysis.results.symmetryRecDesc',
    product: 'Singderm®'
  }
};
const PRIORITY_STYLES: Record<Priority, {
  label: string;
  bg: string;
  text: string;
}> = {
  high: {
    label: 'faceAnalysis.results.priorityHigh',
    bg: 'bg-red-50 border-red-100',
    text: 'text-red-700'
  },
  medium: {
    label: 'faceAnalysis.results.priorityMedium',
    bg: 'bg-brand-copper/10 border-brand-copper/20',
    text: 'text-brand-darkCopper'
  },
  low: {
    label: 'faceAnalysis.results.priorityLow',
    bg: 'bg-gray-50 border-gray-200',
    text: 'text-gray-600'
  }
};
export function ResultsStep({
  imageSrc,
  landmarks,
  onRetake
}: ResultsStepProps) {
  const {
    t
  } = useLanguage();
  const analysis = useMemo(() => analyzeFace(landmarks), [landmarks]);
  const [intensity, setIntensity] = useState(1);
  const intensityLabel = intensity < 0.4 ? t('faceAnalysis.results.intensitySubtle') : intensity < 0.8 ? t('faceAnalysis.results.intensityModerate') : t('faceAnalysis.results.intensityStrong');
  const hasLandmarks = landmarks.length === 68;
  return <motion.div initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: 1,
    y: 0
  }} className="max-w-6xl mx-auto space-y-8" data-id="element-400">
      {/* Page title */}
      <div className="text-center" data-id="element-401">
        <h2 className="text-3xl md:text-4xl font-light text-gray-900 mb-3" data-id="element-402">
          {t('faceAnalysis.results.title')}
        </h2>
        <p className="text-gray-600 text-base md:text-lg max-w-2xl mx-auto" data-id="element-403">
          {t('faceAnalysis.results.subtitle')}
        </p>
      </div>

      {/* Hero Score */}
      <ScoreCard analysis={analysis} data-id="element-404" />

      {/* Slider + Heatmap row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-405">
        {/* Before/After + intensity */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6 space-y-5" data-id="element-406">
          <BeforeAfterSlider imageSrc={imageSrc} landmarks={landmarks} analysis={analysis} intensity={intensity} data-id="element-407" />

          {/* Intensity slider */}
          <div data-id="element-408">
            <div className="flex justify-between items-center mb-2" data-id="element-409">
              <span className="text-sm font-medium text-gray-700" data-id="element-410">
                {t('faceAnalysis.results.intensity')}
              </span>
              <span className="text-xs font-semibold text-brand-darkCopper bg-brand-copper/10 px-2 py-0.5 rounded-full" data-id="element-411">
                {intensityLabel}
              </span>
            </div>
            <input type="range" min={0} max={1} step={0.01} value={intensity} onChange={e => setIntensity(Number(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-copper" data-id="element-412" />
            <div className="flex justify-between text-[10px] text-gray-400 mt-1 font-medium tracking-wider uppercase" data-id="element-413">
              <span data-id="element-414">{t('faceAnalysis.results.intensitySubtle')}</span>
              <span data-id="element-415">{t('faceAnalysis.results.intensityModerate')}</span>
              <span data-id="element-416">{t('faceAnalysis.results.intensityStrong')}</span>
            </div>
          </div>

          {/* Actions */}
          <div className="flex flex-col sm:flex-row gap-3" data-id="element-417">
            <button onClick={onRetake} className="flex-1 flex items-center justify-center px-6 py-3 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors font-medium" data-id="element-418">
              <RefreshCw className="w-4 h-4 mr-2" data-id="element-419" />
              {t('faceAnalysis.results.retake')}
            </button>
            <Link to="/contact" className="flex-1 flex items-center justify-center px-6 py-3 rounded-full bg-brand-copper text-white hover:bg-brand-darkCopper transition-colors font-medium shadow-lg shadow-brand-copper/20" data-id="element-420">
              <Calendar className="w-4 h-4 mr-2" data-id="element-421" />
              {t('faceAnalysis.results.bookConsultation')}
            </Link>
          </div>
        </div>

        {/* Face Heatmap */}
        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6" data-id="element-422">
          <div className="flex items-center mb-4" data-id="element-423">
            <MapIcon className="w-5 h-5 text-brand-copper mr-2" data-id="element-424" />
            <div data-id="element-425">
              <h3 className="text-base font-semibold text-gray-900" data-id="element-426">
                {t('faceAnalysis.results.faceMap')}
              </h3>
              <p className="text-xs text-gray-500" data-id="element-427">
                {t('faceAnalysis.results.faceMapDesc')}
              </p>
            </div>
          </div>

          {hasLandmarks ? <FaceHeatmap imageSrc={imageSrc} landmarks={landmarks} analysis={analysis} data-id="element-428" /> : <div className="aspect-[3/4] rounded-2xl bg-gray-100 flex items-center justify-center text-gray-400 text-sm" data-id="element-429">
              No face map data
            </div>}

          {/* Legend */}
          <div className="grid grid-cols-3 gap-2 mt-4" data-id="element-430">
            <div className="flex items-center text-xs text-gray-600" data-id="element-431">
              <span className="w-3 h-3 rounded-full bg-green-400 mr-1.5" data-id="element-432"></span>
              {t('faceAnalysis.results.faceMapLegendGood')}
            </div>
            <div className="flex items-center text-xs text-gray-600" data-id="element-433">
              <span className="w-3 h-3 rounded-full bg-amber-400 mr-1.5" data-id="element-434"></span>
              {t('faceAnalysis.results.faceMapLegendImprove')}
            </div>
            <div className="flex items-center text-xs text-gray-600" data-id="element-435">
              <span className="w-3 h-3 rounded-full bg-red-400 mr-1.5" data-id="element-436"></span>
              {t('faceAnalysis.results.faceMapLegendAttention')}
            </div>
          </div>
        </div>
      </div>

      {/* Recommendations */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-5 sm:p-6" data-id="element-437">
        <h3 className="text-lg font-semibold text-gray-900 mb-5 flex items-center" data-id="element-438">
          <CheckCircle2 className="w-5 h-5 mr-2 text-brand-copper" data-id="element-439" />
          {t('faceAnalysis.results.recommendations')}
        </h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-440">
          {analysis.recommendations.map((rec, idx) => {
          const meta = RECOMMENDATION_META[rec.id];
          if (!meta) return null;
          const pstyle = PRIORITY_STYLES[rec.priority];
          return <motion.div key={rec.id} initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: idx * 0.08
          }} className="bg-brand-bg/30 p-4 rounded-2xl border border-gray-100" data-id="element-441">
                <div className="flex justify-between items-start mb-2 gap-2" data-id="element-442">
                  <div className="flex items-center gap-2 flex-wrap" data-id="element-443">
                    <h4 className="font-semibold text-gray-900 text-sm" data-id="element-444">
                      {t(meta.titleKey)}
                    </h4>
                    <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded border ${pstyle.bg} ${pstyle.text}`} data-id="element-445">
                      {t(pstyle.label)}
                    </span>
                  </div>
                  <span className="text-xs font-medium bg-brand-copper/10 text-brand-darkCopper px-2 py-1 rounded-md flex-shrink-0" data-id="element-446">
                    {meta.product}
                  </span>
                </div>
                <p className="text-sm text-gray-600 leading-relaxed" data-id="element-447">
                  {t(meta.descKey)}
                </p>
              </motion.div>;
        })}
        </div>
      </div>

      <div className="bg-orange-50 border border-orange-100 rounded-xl p-4 flex items-start" data-id="element-448">
        <ShieldAlert className="w-5 h-5 text-orange-400 mr-3 flex-shrink-0 mt-0.5" data-id="element-449" />
        <p className="text-xs text-orange-800 leading-relaxed" data-id="element-450">
          {t('faceAnalysis.results.disclaimer')}
        </p>
      </div>
    </motion.div>;
}